<svelte:options runes={true} />

<script lang="ts">
  // Keep these imported in the same order.
  import '@shoelace-style/shoelace/dist/themes/light.css';
  //
  import '@shoelace-style/shoelace/dist/themes/dark.css';
  //
  import './app.css';

  import { ColorMode } from '@color';
  import { isDevelopment, SHOW_INTRO_IN_DEV } from '@env';
  import { localeManager, prefs } from '@reactive';
  import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path.js';
  import DocView from './components/DocView.svelte';
  import Intro from './components/Intro.svelte';

  setBasePath('/node_modules/@shoelace-style/shoelace/dist');

  // Decide whether to show the intro screen based on environment flags and stored demo password expiry.
  // Rules:
  // 1. If there's a future demoPasswordExpiry timestamp, skip intro.
  // 2. Else in production build, show intro.
  // 3. Else in development, show only if SHOW_INTRO_IN_DEV flag enabled.
  function computeShowIntro(): boolean {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const expiry = prefs.demoPasswordExpiry;
    if (expiry && expiry > nowSeconds) return false;
    // No valid unexpired session; apply environment rules.
    if (!isDevelopment()) return true;
    return SHOW_INTRO_IN_DEV;
  }

  let showIntro = $state(computeShowIntro());

  // Recompute if preferences change (expiry) or color mode changes has no effect; but track expiry.
  $effect(() => {
    // Re-evaluate when demoPasswordExpiry changes.
    void prefs.demoPasswordExpiry; // reactive dependency only
    showIntro = computeShowIntro();
  });

  function handleGetStarted() {
    showIntro = false;
  }

  $effect(() => {
    // Set the `direction` style attribute in the `:root` stylesheet to `rtl`:
    document.documentElement.style.setProperty('direction', localeManager.textDirection);
  });

  $effect(() => {
    setShoelaceTheme(prefs.colorMode);
  });

  function setShoelaceTheme(mode: ColorMode) {
    const htmlElement = document.documentElement;

    if (mode === ColorMode.DARK) {
      htmlElement.classList.remove('sl-theme-light');
      htmlElement.classList.add('sl-theme-dark');
    } else {
      htmlElement.classList.remove('sl-theme-dark');
      htmlElement.classList.add('sl-theme-light');
    }
  }
</script>

<style>
  /* Turn off overscrolling */
  :global(html, body) {
    overscroll-behavior: none;
  }
  :global(*) {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
</style>

<main id="Main" style="position: absolute; top: 0; left: 0; width: 100vw; height: 100vh;">
  {#if showIntro}
    <Intro getStarted={handleGetStarted} />
  {:else}
    <DocView />
  {/if}
</main>
